import React from 'react';
import { Row, Col, Card, Progress, Typography, Empty, Spin, Statistic } from 'antd';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { PautaPorRegimento } from '../../../../types/dashboard/cppd.types';

const { Text, Title } = Typography;

interface PautasChartProps {
  data: PautaPorRegimento[];
  loading?: boolean;
  error?: string | null;
  height?: number;
}

const CPPDPautasChart: React.FC<PautasChartProps> = ({
  data,
  loading = false,
  error = null,
  height = 300
}) => {
  if (loading) {
    return <div className="loading-container"><Spin tip="Carregando..." /></div>;
  }

  if (error) {
    return <Empty description={error} />;
  }

  if (!data || data.length === 0) {
    return <Empty description="Nenhum dado disponível" />;
  }

  // Verificar se há dados com valores maiores que zero
  const hasData = data.some(item => item.count > 0);
  if (!hasData) {
    return <Empty description="Nenhuma pauta encontrada" />;
  }

  // Calcular total para percentuais
  const total = data.reduce((sum, item) => sum + item.count, 0);

  // Cores para diferentes regimentos
  const COLORS = ['#7F56D9', '#2E90FA', '#F63D68', '#039855', '#F79009', '#6941C6'];

  // Preparar dados para o gráfico
  const chartData = data.map(item => ({
    name: item.regimentoLabel,
    value: item.count,
    percentual: (item.count / total) * 100
  }));

  // Preparar dados para a tabela de estatísticas
  const statsData = data.map((item, index) => ({
    name: item.regimentoLabel,
    value: item.count,
    percentual: (item.count / total) * 100,
    color: COLORS[index % COLORS.length]
  }));

  return (
    <div className="pautas-regimento-stats">
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <ResponsiveContainer width="100%" height={height}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${percent.toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                paddingAngle={2}
                animationDuration={500}
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={COLORS[index % COLORS.length]} 
                    strokeWidth={1}
                  />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name) => [
                  `${value} pautas (${chartData.find(item => item.name === name)?.percentual.toFixed(1)}%)`, 
                  name
                ]}
                labelFormatter={() => 'Distribuição por Regimento'}
              />
              <Legend 
                layout="vertical" 
                verticalAlign="middle" 
                align="right"
                formatter={(value, entry, index) => {
                  const item = chartData.find(d => d.name === value);
                  return `${value}`;
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Col>
        <Col xs={24} lg={12}>
          <div className="stats-container">
            <Title level={4} style={{ marginBottom: 16 }}>Detalhes por Regimento</Title>
            {statsData.map((item, index) => (
              <div key={index} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong style={{ color: item.color }}>{item.name}</Text>
                  <Statistic 
                    value={item.value} 
                    suffix={`pautas (${item.percentual.toFixed(1)}%)`}
                    valueStyle={{ fontSize: 14 }}
                  />
                </div>
                <Progress 
                  percent={item.percentual} 
                  showInfo={false} 
                  strokeColor={item.color}
                  strokeWidth={8}
                />
              </div>
            ))}
            <Statistic 
              title="Total de Pautas" 
              value={total}
              style={{ marginTop: 16 }}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default CPPDPautasChart;